import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Bottom-sheet form for reverting (or cancelling) a delivered / paid
 * sale. Mirrors the desktop Revert modal in
 * frontend/src/pages/sales/SalesActionModals.jsx — same fields, same
 * consequences summary, same colour vocabulary (orange = revert,
 * red = cancel) — but laid out as a slide-up sheet so it works one-
 * handed on mobile and so the textarea-keyboard doesn't push a centred
 * modal off-screen.
 *
 * Usage:
 *   <RevertSaleSheet
 *     sale={sale}
 *     onClose={() => setOpen(false)}
 *     onSuccess={(updated) => { setSale(updated); setOpen(false); }}
 *   />
 *
 * `revertFn` defaults to api/sales#revertSale; pass a stub in tests.
 *
 * Backend gates this to delivered/paid AND non-fiscalised sales — the
 * caller is responsible for only mounting this sheet when those
 * conditions hold (same way the desktop only opens its modal from a
 * row-action menu that's also gated). The sheet still surfaces
 * backend 400/403 errors inline as a defence in depth.
 */

import { revertSale } from '../api/sales';

// Fixed reason list — matches frontend/src/pages/sales/SalesActionModals.jsx
// and backend/app/schemas/sale.py REVERT_REASONS. Keep them in sync if
// the canonical list changes.
const REASONS = [
  'Pricing error',
  'Customer cancellation',
  'Wrong items delivered',
  'Duplicate entry',
  'Other',
];

export default function RevertSaleSheet({ sale, onClose, onSuccess, revertFn = revertSale }) {
  // Default action mirrors desktop — most common case is "rep entered
  // wrong info, send it back to draft." Cancel is the rarer terminal
  // case.
  const [action, setAction] = useState('revert_to_draft');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape so keyboard-driven QA isn't a dead-end.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit() {
    if (!reason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await revertFn(sale.id, { action, reason, notes: notes || undefined });
      onSuccess(updated);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Revert failed');
      setSubmitting(false);
    }
    // Note: we don't reset submitting on success — onSuccess closes the
    // sheet and the component unmounts.
  }

  const isCancel = action === 'cancel';
  // Submit button colour + label switch with action so the rep sees a
  // meaningful difference before they tap. Red is the destructive
  // (terminal) path.
  const submitCls = isCancel
    ? 'bg-red-600 active:bg-red-700'
    : 'bg-orange-500 active:bg-orange-600';
  const submitLabel = submitting
    ? 'Processing…'
    : isCancel
      ? 'Cancel sale'
      : 'Revert to draft';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="revert-title"
      onClick={onClose}
    >
      <div
        // Stop propagation so taps inside the sheet don't bubble to the
        // backdrop close handler.
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-sheet shadow-2xl px-4 pt-3 pb-safe-bottom max-h-[92vh] overflow-y-auto"
        style={{ animation: 'sheet-slide-up 180ms ease-out' }}
      >
        {/* Drag-handle pill — visual affordance, not interactive */}
        <div className="w-10 h-1 bg-gray-300 rounded-pill mx-auto mb-2" />

        <h2 id="revert-title" className="text-title font-bold text-gray-900 mb-1">
          Revert sale
        </h2>

        {/* Sale summary — matches the desktop's amber callout box. */}
        <div className="bg-orange-50 border border-orange-200 rounded-card p-3 mb-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-600 mt-0.5 shrink-0" />
            <div className="text-small text-orange-900">
              <strong>{sale.invoice_number ?? `Sale #${sale.id}`}</strong>
              {sale.customer_name && <> — {sale.customer_name}</>}
              <p className="mt-1 text-caption text-orange-800">
                This will restore stock and convert any payments to customer credit.
              </p>
            </div>
          </div>
        </div>

        {/* Action toggle — two big square buttons that highlight when
            picked. Same visual vocabulary as the desktop modal. */}
        <label className="block text-small font-medium text-gray-700 mb-2">Action *</label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            type="button"
            onClick={() => setAction('revert_to_draft')}
            className={[
              'p-3 rounded-card border-2 text-left transition-colors',
              action === 'revert_to_draft'
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200',
            ].join(' ')}
          >
            <div className="text-small font-semibold text-gray-900">Revert to draft</div>
            <div className="text-caption text-gray-500 mt-0.5">
              Stock back on hold · sale becomes editable
            </div>
          </button>
          <button
            type="button"
            onClick={() => setAction('cancel')}
            className={[
              'p-3 rounded-card border-2 text-left transition-colors',
              action === 'cancel'
                ? 'border-red-400 bg-red-50'
                : 'border-gray-200',
            ].join(' ')}
          >
            <div className="text-small font-semibold text-gray-900">Cancel sale</div>
            <div className="text-caption text-gray-500 mt-0.5">
              Stock fully released · permanent
            </div>
          </button>
        </div>

        {/* Required reason — fixed list, matches REVERT_REASONS on the
            backend. */}
        <label className="block text-small font-medium text-gray-700 mb-1">Reason *</label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full min-h-touch px-3 mb-3 border border-gray-300 rounded-card text-body bg-surface focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        >
          <option value="">Select a reason…</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        {/* Optional notes */}
        <label className="block text-small font-medium text-gray-700 mb-1">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Additional context…"
          className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-card text-body focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />

        {/* Consequences summary — re-renders against the chosen action so
            the rep sees exactly what will happen before they commit. */}
        <div className="bg-gray-50 rounded-card p-3 mb-3 text-caption text-gray-700 space-y-1">
          <p className="font-semibold text-gray-800">Consequences:</p>
          <p>
            • Stock restored:{' '}
            {action === 'revert_to_draft'
              ? 'back on hold for this sale'
              : 'fully released to available'}
          </p>
          <p>• Payments (if any) detached → added as customer credit</p>
          <p>• Ledger entry reversed</p>
          {action === 'revert_to_draft' && (
            <p>• ZIMRA fiscal number cleared — will be reassigned on re-delivery</p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-small text-danger mb-3">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 min-h-touch rounded-card border border-gray-300 bg-white text-gray-700 font-medium disabled:opacity-60"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!reason || submitting}
            className={`flex-1 min-h-touch rounded-card font-semibold text-white text-body disabled:opacity-50 transition-colors flex items-center justify-center gap-2 ${submitCls}`}
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            {submitLabel}
          </button>
        </div>

        {/* Inline keyframes for the slide-up. Scoped here so we don't
            pollute global CSS for a single-use animation. */}
        <style>{`
          @keyframes sheet-slide-up {
            from { transform: translateY(100%); }
            to   { transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
